import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import {
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    Alert,
} from "@mui/material";
import { getLostCustomerStatistics } from "../../api/api";
import { getCurrentUser } from "../../utils/auth";

// Styled Table Cells and Rows
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        fontWeight: "bold",
        textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: "center",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const Customer_Lost_Report = ({ startDate, endDate }) => {
    const chartRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [summaryStats, setSummaryStats] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const user = getCurrentUser();
                setCurrentUser(user);

                const startDateParam = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const endDateParam = endDate ? endDate.format("YYYY-MM-DD") : undefined;
                const response = await getLostCustomerStatistics(startDateParam, endDateParam);
                console.log("Customer Lost Report API Response:", response);
                if (response.success) {
                    console.log("Customer Lost Report Data:", response.data);
                    setData(response.data);
                    setSummaryStats({
                        totalLost: response.total_lost || 0,
                        totalCustomers: response.total_customers || 0,
                        lostPercentage: response.lost_percentage || 0,
                        averageLostPerDay: response.average_lost_per_day || 0,
                    });
                } else {
                    setError(response.message || "Failed to fetch data");
                }
            } catch (err) {
                console.error("Error fetching lost customer statistics:", err);
                setError("Failed to load LOST customer statistics");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (!data || !chartRef.current) return;

        const chartInstance = echarts.init(chartRef.current);

        // Group data by day/date and sum lost_count to avoid duplicates
        const groupedData = {};
        data.forEach((item) => {
            const key = item.date || item.day; // Use date as primary key, fallback to day
            if (!groupedData[key]) {
                groupedData[key] = {
                    day: item.day,
                    date: item.date,
                    lost_count: 0,
                };
            }
            groupedData[key].lost_count += parseInt(item.lost_count) || 0;
        });

        // Convert grouped data to arrays, sorted by date
        const sortedData = Object.values(groupedData).sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA - dateB;
        });

        const categories = sortedData.map((item) => item.day);
        const values = sortedData.map((item) => item.lost_count);

        const options = {
            title: {
                text: `LOST Customers per Day${
                    currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""
                }`,
                left: "center",
                textStyle: { fontSize: 16, fontWeight: "bold" },
            },
            tooltip: {
                trigger: "axis",
                formatter: function (params) {
                    const data = params[0];
                    return `${data.name}<br/>LOST Customers: ${data.value}`;
                },
            },
            xAxis: {
                type: "category",
                data: categories,
                axisLabel: { rotate: 30 },
            },
            yAxis: {
                type: "value",
                name: "LOST Customers",
            },
            series: [
                {
                    data: values,
                    type: "bar",
                    barWidth: "50%",
                    itemStyle: {
                        color: "#d32f2f",
                    },
                    label: {
                        show: true,
                        position: "top",
                        formatter: "{c}",
                    },
                },
            ],
        };

        chartInstance.setOption(options);

        // Responsive resize
        const handleResize = () => chartInstance.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            chartInstance.dispose();
            window.removeEventListener("resize", handleResize);
        };
    }, [data, currentUser]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Summary Stats */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: "error.main",
                }}
            >
                <Typography variant="h6" gutterBottom color="error">
                    LOST Customer Statistics
                    {currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""}
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Total LOST Customers
                        </Typography>
                        <Typography variant="h6" color="error">
                            {summaryStats?.totalLost || 0}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Total Customers
                        </Typography>
                        <Typography variant="h6">
                            {summaryStats?.totalCustomers || 0}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            LOST Percentage
                        </Typography>
                        <Typography variant="h6" color="error">
                            {summaryStats?.lostPercentage || 0}%
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Average LOST per Day
                        </Typography>
                        <Typography variant="h6" color="error">
                            {summaryStats?.averageLostPerDay || 0}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="LOST Customer Report Table"
                >
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Day</StyledTableCell>
                            <StyledTableCell>Date</StyledTableCell>
                            <StyledTableCell>LOST Count</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.filter((row) => parseInt(row.lost_count) > 0).length > 0 ? (
                            data.filter((row) => parseInt(row.lost_count) > 0).map((row) => (
                                <StyledTableRow key={row.date}>
                                    <StyledTableCell>{row.day}</StyledTableCell>
                                    <StyledTableCell>{row.date}</StyledTableCell>
                                    <StyledTableCell>
                                        {parseInt(row.lost_count)}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No LOST customers found in the selected date range
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Chart */}
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                LOST Customers per Day (Bar Chart)
            </Typography>
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 400,
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    boxShadow: 2,
                }}
            />
        </Box>
    );
};

export default Customer_Lost_Report;

